import type { IChangeAction, IChangeActionArg, IChangeActionParameter } from '@models/Forms/IForms';
import { isIsoDateString, isIsoDateNoTimeString } from '@utils/documentUtils';
import {
    evaluateConditionalVariables,
    getVariableReferences,
    resolveFormulaVariableReferences,
    resolveVariableReference,
    resolveVariableReferences,
} from './conditionalVariables';
import { substituteFormulaData, type FormulaTokenValue } from './formulaStringLiteral';
const parseActionParameterValue = (value: unknown, parameter: IChangeActionParameter, actionName: string) => {
    if (value === null && parameter.nullable) return null;
    if (value === null || value === undefined) {
        throw new Error(
            `changeAction "${actionName}" parameter "${parameter.name}" does not accept null or undefined.`,
        );
    }
    const valid =
        parameter.type === 'string'
            ? typeof value === 'string'
            : parameter.type === 'boolean'
              ? typeof value === 'boolean'
              : typeof value === 'number' && Number.isFinite(value);
    if (!valid)
        throw new Error(`changeAction "${actionName}" parameter "${parameter.name}" must be ${parameter.type}.`);
    return value;
};

const parseActionValueLiteral = (rawValue: string, parameter: IChangeActionParameter, actionName: string) => {
    if (parameter.type === 'string') return rawValue;
    if (parameter.type === 'boolean') {
        if (rawValue === 'true') return true;
        if (rawValue === 'false') return false;
        throw new Error(`changeAction "${actionName}" parameter "${parameter.name}" has invalid boolean literal.`);
    }
    if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(rawValue)) {
        throw new Error(`changeAction "${actionName}" parameter "${parameter.name}" has invalid number literal.`);
    }
    const number = Number(rawValue);
    if (!Number.isFinite(number))
        throw new Error(`changeAction "${actionName}" parameter "${parameter.name}" has invalid number literal.`);
    return number;
};

/** Значение события в формуле: дата — числовой timestamp, как в checkCondition. */
const formulaEventValue = (value: unknown): unknown => {
    if (value instanceof Date) return value.getTime();
    if (isIsoDateString(value) || isIsoDateNoTimeString(value)) return new Date(value as string).getTime();
    return value;
};

const ACTION_TOKEN = /\{variables\([^()]*\)\}|variables\([^()]*\)|\{([^{}]+)\}/;

/** Набор значений с вычисляемым attr (calc/date): в нём подстановки — часть выражения. */
export const isEvaluatedSetType = (type: unknown) => type === 'calc' || type === 'date';

/** Поля тела действия, которые вычисляются формулой (условия, фильтры, скрипты, calc/date): в них
 *  параметры встают литералами. Остальное (text-подстановки, сообщения, ключи) — текстом. */
const FORMULA_KEYS = new Set(['condition', 'filter', 'parentFilter', 'countRows', 'expr', 'script', 'setCondition']);
const isFormulaText = (key: string, owner: unknown) =>
    FORMULA_KEYS.has(key) || (key === 'attr' && isEvaluatedSetType((owner as { type?: unknown }).type));

/** Параметр и локальная переменная типизированы: строка — всегда строковый литерал,
 *  числовой текст (`'007'`) не становится числом. */
const typedFormulaValue = (value: unknown): FormulaTokenValue =>
    typeof value === 'string' ? { text: value } : { value };

export const resolveChangeAction = async (
    action: IChangeAction,
    args: IChangeActionArg[] | undefined,
    eventValue: unknown,
    globalVariables: Record<string, unknown>,
    evaluate: (expression: string, variables: Record<string, unknown>) => Promise<unknown>,
    prepareRow: (expression: string) => string,
) => {
    const definitions = new Map<string, IChangeActionParameter>();
    for (const parameter of action.params ?? []) {
        if (!parameter.name || definitions.has(parameter.name))
            throw new Error(`changeAction "${action.name}" has an invalid or duplicate parameter name.`);
        if (!['string', 'number', 'boolean'].includes(parameter.type))
            throw new Error(`changeAction "${action.name}" parameter "${parameter.name}" has an unsupported type.`);
        definitions.set(parameter.name, parameter);
    }

    const supplied = new Map<string, IChangeActionArg>();
    for (const arg of args ?? []) {
        if (!definitions.has(arg.name))
            throw new Error(`changeAction "${action.name}" has unknown argument "${arg.name}".`);
        if (supplied.has(arg.name))
            throw new Error(`changeAction "${action.name}" has duplicate argument "${arg.name}".`);
        const hasValue = Object.prototype.hasOwnProperty.call(arg, 'value');
        const hasExpr = Object.prototype.hasOwnProperty.call(arg, 'expr');
        if (hasValue === hasExpr)
            throw new Error(
                `changeAction "${action.name}" argument "${arg.name}" must have exactly one of value or expr.`,
            );
        if (hasExpr && !arg.expr?.trim())
            throw new Error(`changeAction "${action.name}" argument "${arg.name}" expression cannot be empty.`);
        supplied.set(arg.name, arg);
    }
    if (supplied.size !== definitions.size) {
        const missing = [...definitions.keys()].find((name) => !supplied.has(name));
        throw new Error(`changeAction "${action.name}" is missing argument "${missing}".`);
    }

    const localNames = new Set((action.variables ?? []).map((variable) => variable.name));
    const actionParams: Record<string, unknown> = Object.create(null);
    for (const [name, parameter] of definitions) {
        const arg = supplied.get(name)!;
        for (const reference of getVariableReferences(arg.expr ?? '')) {
            if (localNames.has(reference) && !Object.prototype.hasOwnProperty.call(globalVariables, reference)) {
                throw new Error(
                    `changeAction "${action.name}" argument "${name}" cannot access local variable "${reference}".`,
                );
            }
        }
        let value: unknown;
        if (Object.prototype.hasOwnProperty.call(arg, 'value')) {
            value = parseActionValueLiteral(arg.value!, parameter, action.name);
        } else {
            const expression = substituteFormulaData(arg.expr!, /\{value\}/, () => ({
                value: formulaEventValue(eventValue),
            }));
            const globalExpression = resolveFormulaVariableReferences(expression, globalVariables);
            value = await evaluate(prepareRow(globalExpression), globalVariables);
        }
        actionParams[name] = parseActionParameterValue(value, parameter, action.name);
    }

    const localValues = await evaluateConditionalVariables(
        action.variables,
        globalVariables,
        `changeAction "${action.name}"`,
        evaluate,
        {
            allowShadowInitialValues: true,
            prepareExpression: (expression) =>
                prepareRow(substituteActionParametersAndEvent(expression, actionParams, eventValue)),
        },
    );
    const localOnlyValues = Object.fromEntries((action.variables ?? []).map(({ name }) => [name, localValues[name]]));
    return substituteActionBody(action.changes, actionParams, localOnlyValues, globalVariables, action.name);
};

const substituteActionParametersAndEvent = (text: string, params: Record<string, unknown>, eventValue: unknown) =>
    substituteFormulaData(text, ACTION_TOKEN, (_token, [name]) => {
        if (name === undefined) return undefined;
        if (Object.prototype.hasOwnProperty.call(params, name)) return typedFormulaValue(params[name]);
        if (name === 'value') return { value: formulaEventValue(eventValue) };
        return undefined;
    });

const substituteActionBody = <T>(
    value: T,
    params: Record<string, unknown>,
    locals: Record<string, unknown>,
    globalVariables: Record<string, unknown>,
    actionName: string,
    formula = false,
): T => {
    if (typeof value === 'string') {
        const resolve = (token: string, parameter: string | undefined): FormulaTokenValue => {
            if (parameter !== undefined) {
                return Object.prototype.hasOwnProperty.call(params, parameter)
                    ? typedFormulaValue(params[parameter])
                    : undefined;
            }
            const reference = getVariableReferences(token)[0];
            if (!reference || !Object.prototype.hasOwnProperty.call(locals, reference)) return undefined;
            return formula
                ? typedFormulaValue(resolveVariableReference(token, locals))
                : { raw: resolveVariableReferences(token, locals, true) };
        };
        if (formula)
            return substituteFormulaData(value, ACTION_TOKEN, (token, [parameter]) => resolve(token, parameter)) as T;
        return value.replace(new RegExp(ACTION_TOKEN.source, 'g'), (token, parameter?: string) => {
            const resolved = resolve(token, parameter);
            if (resolved === undefined) return token;
            if ('raw' in resolved) return resolved.raw;
            return 'text' in resolved ? resolved.text : String(resolved.value);
        }) as T;
    }
    if (Array.isArray(value))
        return value.map((item) =>
            substituteActionBody(item, params, locals, globalVariables, actionName, formula),
        ) as T;
    if (value && typeof value === 'object') {
        const isCallAction = Object.prototype.hasOwnProperty.call(value, 'callAction');
        return Object.fromEntries(
            Object.entries(value).map(([key, item]) => {
                if (isCallAction && key === 'callAction' && item && typeof item === 'object') {
                    const call = substituteActionBody(item, params, {}, globalVariables, actionName) as {
                        args?: IChangeActionArg[];
                    };
                    for (const arg of call.args ?? []) {
                        for (const reference of getVariableReferences(arg.expr ?? '')) {
                            if (
                                Object.prototype.hasOwnProperty.call(locals, reference) &&
                                !Object.prototype.hasOwnProperty.call(globalVariables, reference)
                            ) {
                                throw new Error(
                                    `changeAction "${actionName}" callAction args cannot access local variable "${reference}".`,
                                );
                            }
                        }
                    }
                    return [key, call];
                }
                return [
                    key,
                    substituteActionBody(item, params, locals, globalVariables, actionName, isFormulaText(key, value)),
                ];
            }),
        ) as T;
    }
    return value;
};
